import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { NavLink, useLocation, useParams, useNavigate } from "react-router-dom";
import { IoReorderThreeOutline } from "react-icons/io5";
import { HiOutlineChevronDown, HiOutlineChevronUp } from "react-icons/hi";
import SidebarLinks, {
  SidebarLinksForUser,
} from "../../lib/constants/SidebarLinks";
import "../../components/common/text-color/textColor.css";
import { useSelector } from "react-redux";
import { FiLock } from "react-icons/fi";

const linkClass =
  "flex items-center gap-3 px-3 py-1.5 rounded-lg transition-all duration-200 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-[#1e1b32] dark:hover:text-white text-gray-700 dark:text-gray-300 text-sm font-medium";

const activeRouteClass =
  "bg-blue-50 text-blue-600 dark:bg-[#1e1b32] dark:text-white font-semibold";

export default function Sidebar() {
  const { DASHBOARD_SIDEBAR_LINKS, DASHBOARD_SIDEBAR_BOTTOM_LINKS } =
    SidebarLinks();
  const navigate = useNavigate();
  const params = useParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const drawerRef = useRef(null);

  // Close drawer when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobile &&
        isDrawerOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target)
      ) {
        setDrawerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, isDrawerOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const toggleDrawer = () => {
    setDrawerOpen(!isDrawerOpen);
  };

  const toggleSubmenu = (key) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const SidebarLink = ({ link }) => {
    const { pathname } = useLocation();
    const isSubMenuOpen = openSubmenus[link.key] || false;
    const { isOwnerAdmin } = useSelector((state) => state.rolePermission);
    const userRole = localStorage.getItem("role");

    if (link.role && link.role !== userRole) {
      return null;
    }

    if (isOwnerAdmin) {
      // Owner Admin sees everything else
    } else {
      // online-store;
      if (link.key === "online-store") {
        // Hide for resellers
        if (userRole === "reseller") {
          return null;
        }
      }

      if (link.key === "request-business") {
        if (userRole !== "reseller") {
          return null;
        }
      }

      // approved-businesses - only for resellers
      if (link.key === "approved-businesses") {
        if (userRole !== "reseller") {
          return null;
        }
      }

      // my-catalog - only for resellers
      if (link.key === "my-catalog") {
        if (userRole !== "reseller") {
          return null;
        }
      }

      // store-settings - only for resellers
      if (link.key === "store-settings") {
        if (userRole !== "reseller") {
          return null;
        }
      }

      if (link.key === "reseller-orders") {
        if (userRole !== "reseller") {
          return null;
        }
      }

      if (link.key === "requested-resellers") {
        if (userRole !== "owner_admin") {
          return null;
        }
      }

      // approved-resellers - only for owner_admin
      if (link.key === "approved-resellers") {
        if (userRole !== "owner_admin") {
          return null;
        }
      }

      // Hide certain links for resellers
      if (userRole === "reseller") {
        const hiddenKeys = ["inventory", "warehouse", "cashBook", "report"];
        if (hiddenKeys.includes(link.key)) {
          return null;
        }
      }
    }

    const handleLinkClick = () => {
      if (isMobile) {
        setDrawerOpen(false);
      }
    };

    const renderSubMenu = () => {
      const userRole = localStorage.getItem("role");

      return (
        <div className='ml-8 pl-2 border-l-2 border-gray-200 dark:border-gray-700 mt-1 space-y-1'>
          {link.submenu
            .filter((sublink) => {
              // Hide manufacture if warehouse module is not enabled
              // Submenu items skip warehouseModule check for Owner Admin
              if (
                !isOwnerAdmin &&
                link.key === "bills" &&
                sublink.key === "manufacture"
              ) {
                return false;
              }

              // Reseller-specific filters
              if (userRole === "reseller") {
                // In bills, show only sale
                if (link.key === "bills" && sublink.key !== "sale") {
                  return false;
                }

                // In buddies, show only customers and suppliers (hide employees)
                if (link.key === "buddies" && sublink.key === "employees") {
                  return false;
                }
              }

              return true;
            })
            .map((sublink) => {
              if (sublink.isLocked) {
                return (
                  <div
                    key={sublink.key}
                    onClick={() =>
                      navigate(`/${params?.bussinessUuid}/dashboard/subscriber`)
                    }
                    className={classNames(
                      linkClass,
                      "group relative cursor-pointer hover:bg-amber-50/50 dark:hover:bg-amber-900/10",
                    )}
                    title='Upgrade your plan to access this feature'
                  >
                    <span className='text-lg opacity-60'>{sublink.icon}</span>
                    <span className='flex-1 opacity-60'>{sublink.label}</span>
                    <FiLock className='text-amber-500 animate-pulse' />
                  </div>
                );
              }
              return (
                <NavLink
                  key={sublink.key}
                  to={`/${params?.bussinessUuid}${link.path}/${sublink.path}`}
                  className={classNames(
                    pathname ===
                      `/${params?.bussinessUuid}${link.path}/${sublink.path}`
                      ? activeRouteClass
                      : "",
                    linkClass,
                  )}
                  onClick={handleLinkClick}
                >
                  <span className='text-lg'>{sublink.icon}</span>
                  <span className='flex-1'>{sublink.label}</span>
                </NavLink>
              );
            })}
        </div>
      );
    };

    return (
      <div className='mb-1'>
        {link.submenu ? (
          <div>
            <div
              id={isSubMenuOpen ? "toggle-" + link?.key : ""}
              className={classNames(
                "cursor-pointer flex items-center justify-between",
                linkClass,
                pathname.includes(`/${params?.bussinessUuid}${link.path}`) &&
                  !isSubMenuOpen
                  ? activeRouteClass
                  : "",
              )}
              onClick={() => {
                toggleSubmenu(link.key);
                if (isMobile && !isSubMenuOpen) {
                  handleLinkClick();
                }
              }}
            >
              <div className='flex items-center gap-3'>
                <span className='text-lg'>{link.icon}</span>
                <span>{link.label}</span>
              </div>
              {isSubMenuOpen ? (
                <HiOutlineChevronUp className='text-gray-500 dark:text-gray-400' />
              ) : (
                <HiOutlineChevronDown className='text-gray-500 dark:text-gray-400' />
              )}
            </div>
            {isSubMenuOpen && renderSubMenu()}
          </div>
        ) : link.isLocked ? (
          <div
            onClick={() =>
              navigate(`/${params?.bussinessUuid}/dashboard/subscriber`)
            }
            className={classNames(
              linkClass,
              "group relative cursor-pointer hover:bg-amber-50/50 dark:hover:bg-amber-900/10",
            )}
            title='Upgrade your plan to access this feature'
          >
            <div className='flex items-center gap-3 flex-1 opacity-60'>
              <span className='text-lg'>{link.icon}</span>
              <span>{link.label}</span>
            </div>
            <FiLock className='text-amber-500 animate-pulse' />
          </div>
        ) : (
          <NavLink
            to={`/${params?.bussinessUuid}${link.path}`}
            className={classNames(
              pathname === `/${params?.bussinessUuid}${link.path}`
                ? activeRouteClass
                : "",
              linkClass,
            )}
            onClick={handleLinkClick}
          >
            <span className='text-lg'>{link.icon}</span>
            <span>{link.label}</span>
          </NavLink>
        )}
      </div>
    );
  };

  if (isMobile) {
    return (
      <>
        <button
          className='text-white focus:ring-4 focus:ring-blue-300 font-medium text-sm px-5 py-2.5 dark:text-blue-600 dark:hover:text-blue-700 focus:outline-none dark:focus:ring-blue-800 flex flex-col items-start dark:bg-[#170f2a]'
          type='button'
          onClick={toggleDrawer}
        >
          <IoReorderThreeOutline className='text-gray-600 dark:text-gray-300 text-2xl' />
        </button>

        <div
          ref={drawerRef}
          className={`fixed top-0 left-0 z-40 h-screen w-72 transition-all duration-300 ease-in-out ${
            isDrawerOpen ? "translate-x-0" : "-translate-x-full"
          } bg-white dark:bg-[#170f2a] border-r border-gray-200 dark:border-gray-800 shadow-xl`}
          id='drawer-disabled-backdrop'
          tabIndex='-1'
        >
          <div className='flex flex-col h-full'>
            <div>
              <div className='flex items-center justify-between'>
                <button
                  style={{ "margin-left": "15rem" }}
                  type='button'
                  onClick={toggleDrawer}
                  className='text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 absolute top-2.5 end-2.5 inline-flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white'
                >
                  <svg
                    className='w-3 h-3'
                    aria-hidden='true'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 14 14'
                  >
                    <path
                      stroke='currentColor'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6'
                    />
                  </svg>
                  <span className='sr-only'>Close menu</span>
                </button>
              </div>
            </div>

            <div
              className='py-4 overflow-y-auto'
              style={{ maxHeight: 567, overflowY: "auto" }}
            >
              <div className='space-y-1'>
                {DASHBOARD_SIDEBAR_LINKS.map((link) => (
                  <SidebarLink key={link.key} link={link} />
                ))}
              </div>
            </div>

            <div className='p-4 border-t border-gray-200 dark:border-gray-800'>
              <div className='space-y-1'>
                {DASHBOARD_SIDEBAR_BOTTOM_LINKS.map((link) => (
                  <SidebarLink key={link.key} link={link} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  } else {
    return (
      <div className='bg-white w-64 p-6 flex flex-col border-r border-gray-200 dark:border-gray-800 dark:bg-[#170f2a] h-screen sticky top-0'>
        <div className='flex items-center gap-2 px-1 py-3 relative -top-5'>
          <img
            src='https://iconsbucket1.s3.us-east-1.amazonaws.com/logo.png'
            alt='dukanbuddy'
            className='h-8 mx-auto dark:hidden'
          />
          <div className='mx-auto hidden dark:block'>
            <svg
              id='Layer_2'
              data-name='Layer 2'
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 258.71 36.83'
              className='h-7'
            >
              <defs>
                <style>{`
              .cls-1 {
                fill: #fcffff;
                stroke: #170f2a;
                stroke-width: 0.5px;
              }
              .cls-1, .cls-2, .cls-3 {
                stroke-width: 0px;
              }
              .cls-2 {
                fill: #fcffff;
                stroke: #170f2a;
                stroke-width: 0.5px;
              }
              .cls-3 {
                fill: #fcffff;
                stroke: #170f2a;
                stroke-width: 0.5px;
              }
              .cls-b {
                fill: #0000ff;
                stroke: #170f2a;
                stroke-width: 0.5px;
              }
            `}</style>
              </defs>
              <g id='Layer_1-2' data-name='Layer 1'>
                <g>
                  <path
                    className='cls-2'
                    d='m67.61,6.45v23.33h-3.77v-2.17c-1.32,1.6-3.27,2.39-5.56,2.39-4.87,0-8.49-3.39-8.49-8.61s3.62-8.58,8.49-8.58c2.17,0,4.09.72,5.41,2.26V6.45h3.93Zm-3.87,14.93c0-3.21-2.17-5.25-5-5.25s-5,2.04-5,5.25,2.17,5.25,5,5.25,5-2.04,5-5.25Z'
                  />
                  <path
                    className='cls-2'
                    d='m89.02,12.99v16.79h-3.74v-2.14c-1.26,1.54-3.24,2.36-5.41,2.36-4.31,0-7.23-2.36-7.23-7.42v-9.59h3.93v9.05c0,3.05,1.45,4.5,3.93,4.5,2.73,0,4.59-1.7,4.59-5.06v-8.49h3.93Z'
                  />
                  <path
                    className='cls-2'
                    d='m101.03,22.57l-2.92,2.77v4.43h-3.93V6.45h3.93v14.08l10-9.4h4.72l-8.86,8.9,7.67,9.75h-4.78l-5.82-7.2Z'
                  />
                  <path
                    className='cls-2'
                    d='m127.9,19.93v9.84h-3.71v-2.04c-.94,1.45-2.77,2.26-5.28,2.26-3.84,0-6.26-2.11-6.26-5.03s1.89-5,6.98-5h4.34v-.25c0-2.3-1.38-3.65-4.18-3.65-1.89,0-3.84.63-5.09,1.67l-1.54-2.86c1.79-1.38,4.4-2.07,7.1-2.07,4.84,0,7.64,2.29,7.64,7.14Zm-3.93,4.59v-1.95h-4.06c-2.67,0-3.4,1.01-3.4,2.23,0,1.41,1.19,2.33,3.21,2.33s3.58-.88,4.24-2.61Z'
                  />
                  <path
                    className='cls-2'
                    d='m149.47,20.15v9.62h-3.93v-9.12c0-2.99-1.48-4.43-3.99-4.43-2.8,0-4.68,1.7-4.68,5.06v8.49h-3.93V12.99h3.74v2.17c1.29-1.54,3.33-2.36,5.78-2.36,4.02,0,7.01,2.29,7.01,7.36Z'
                  />
                  <path
                    className='cls-2'
                    d='m194.64,12.99v16.79h-3.74v-2.14c-1.26,1.54-3.24,2.36-5.41,2.36-4.31,0-7.23-2.36-7.23-7.42v-9.59h3.93v9.05c0,3.05,1.45,4.5,3.93,4.5,2.73,0,4.59-1.7,4.59-5.06v-8.49h3.93Z'
                  />
                  <path
                    className='cls-2'
                    d='m216.24,6.45v23.33h-3.77v-2.17c-1.32,1.6-3.27,2.39-5.56,2.39-4.87,0-8.49-3.39-8.49-8.61s3.62-8.58,8.49-8.58c2.17,0,4.09.72,5.41,2.26V6.45h3.93Zm-3.87,14.93c0-3.21-2.17-5.25-5-5.25s-5,2.04-5,5.25,2.17,5.25,5,5.25,5-2.04,5-5.25Z'
                  />
                  <path
                    className='cls-2'
                    d='m237.83,6.45v23.33h-3.77v-2.17c-1.32,1.6-3.27,2.39-5.56,2.39-4.87,0-8.49-3.39-8.49-8.61s3.62-8.58,8.49-8.58c2.17,0,4.09.72,5.41,2.26V6.45h3.93Zm-3.87,14.93c0-3.21-2.17-5.25-5-5.25s-5,2.04-5,5.25,2.17,5.25,5,5.25,5-2.04,5-5.25Z'
                  />
                  <path
                    className='cls-2'
                    d='m258.71,12.99l-7.86,18.14c-1.54,3.8-3.68,4.97-6.48,4.97-1.67,0-3.43-.57-4.5-1.54l1.57-2.89c.75.72,1.82,1.16,2.89,1.16,1.38,0,2.2-.66,2.96-2.33l.28-.66-7.32-16.85h4.09l5.28,12.42,5.31-12.42h3.77Z'
                  />
                  <path
                    className='cls-2'
                    d='m170.44,18.33c1.7-.91,2.83-2.58,2.83-4.84,0-3.49-2.83-5.72-8.11-5.72h-10.31v22.01h10.94c5.66,0,8.61-2.2,8.61-5.97,0-2.86-1.6-4.72-3.96-5.47Z'
                  />
                  <g>
                    <g>
                      <circle
                        className='cls-b'
                        cx='166.59'
                        cy='14.34'
                        r='2.84'
                      />
                      <path
                        className='cls-b'
                        d='m159.54,19.13h-.8c-.27,0-.49-.22-.49-.49v-8.6c0-.27.22-.49.49-.49h.8c1.52,0,3.47.68,3.83,2.67-.48.64-.68,1.23-.68,2.12s.2,1.56.67,2.11c-.39,2.06-2.43,2.68-3.83,2.68Z'
                      />
                    </g>
                    <g>
                      <circle
                        className='cls-b'
                        cx='166.59'
                        cy='23.2'
                        r='2.84'
                      />
                      <path
                        className='cls-b'
                        d='m159.54,27.99h-.8c-.27,0-.49-.22-.49-.49v-8.6c0-.27.22-.49.49-.49h.8c1.52,0,3.47.68,3.83,2.67-.48.64-.68,1.23-.68,2.12s.2,1.56.67,2.11c-.39,2.06-2.43,2.68-3.83,2.68Z'
                      />
                    </g>
                  </g>
                  <polygon
                    className='cls-1'
                    points='10.63 2 5.09 2 .36 10.41 7.8 10.41 10.63 2'
                  />
                  <polygon
                    className='cls-1'
                    points='17.42 10.41 24.95 10.41 24.01 2 18.37 2 17.42 10.41'
                  />
                  <polygon
                    className='cls-1'
                    points='17.91 .97 24.47 .97 31.03 .97 36.93 .97 36.68 0 5.7 0 5.45 .97 11.35 .97 17.91 .97'
                  />
                  <polygon
                    className='cls-1'
                    points='37.29 2 31.75 2 34.58 10.41 42.01 10.41 37.29 2'
                  />
                  <polygon
                    className='cls-1'
                    points='8.88 10.41 16.39 10.41 17.33 2 11.72 2 8.88 10.41'
                  />
                  <path
                    className='cls-1'
                    d='m26.04,13.01c0,2.11,1.72,3.83,3.83,3.83s3.83-1.72,3.83-3.83v-1.56h-7.65v1.56Z'
                  />
                  <path
                    className='cls-1'
                    d='m17.36,13.01c0,2.11,1.72,3.83,3.83,3.83s3.83-1.72,3.83-3.83v-1.56h-7.65v1.56Z'
                  />
                  <polygon
                    className='cls-1'
                    points='25.99 10.41 33.49 10.41 30.66 2 25.04 2 25.99 10.41'
                  />
                  <path
                    className='cls-1'
                    d='m8.68,13.01c0,2.11,1.72,3.83,3.83,3.83s3.83-1.72,3.83-3.83v-1.56h-7.65v1.56Z'
                  />
                  <path
                    className='cls-1'
                    d='m34.21,15.18c-.8,1.59-2.44,2.68-4.34,2.68s-3.54-1.09-4.34-2.68c-.8,1.59-2.44,2.68-4.34,2.68s-3.54-1.09-4.34-2.68c-.8,1.59-2.44,2.68-4.34,2.68s-3.54-1.09-4.34-2.68c-.8,1.59-2.44,2.68-4.34,2.68v16.95c0,1.11.9,2.02,2.02,2.02h30.68c1.11,0,2.02-.9,2.02-2.02v-16.95c-1.89,0-3.54-1.09-4.34-2.68Zm-19.75,18.38c0,.28-.23.51-.51.51h-6.68c-.28,0-.51-.23-.51-.51v-11.8c0-.28.23-.51.51-.51h6.68c.28,0,.51.23.51.51v11.8Zm21.17,0c0,.28-.23.51-.51.51h-17.73c-.28,0-.51-.23-.51-.51v-11.8c0-.28.23-.51.51-.51h17.73c.28,0,.51.23.51.51v11.8Z'
                  />
                  <path
                    className='cls-1'
                    d='m34.73,13.01c0,2.11,1.72,3.83,3.83,3.83s3.83-1.72,3.83-3.83v-1.56h-7.65v1.56Z'
                  />
                  <path
                    className='cls-1'
                    d='m7.65,13.01v-1.56H0v1.56c0,2.11,1.72,3.83,3.83,3.83s3.83-1.72,3.83-3.83Z'
                  />
                  <rect
                    className='cls-2'
                    x='17.89'
                    y='22.27'
                    width='16.7'
                    height='10.78'
                  />
                  <rect
                    className='cls-2'
                    x='7.78'
                    y='22.27'
                    width='5.65'
                    height='10.78'
                  />
                </g>
              </g>
            </svg>
          </div>
        </div>

        <div
          className='flex-1 overflow-y-auto scroll-w-6'
          style={{ maxHeight: "calc(100vh - 180px)" }}
        >
          <div className='space-y-1'>
            {DASHBOARD_SIDEBAR_LINKS.map((link) => (
              <SidebarLink key={link.key} link={link} />
            ))}
          </div>
        </div>

        <div className='mt-auto pt-2 border-t border-gray-200 dark:border-gray-700'>
          <div className='space-y-1'>
            {DASHBOARD_SIDEBAR_BOTTOM_LINKS.map((link) => (
              <SidebarLink key={link.key} link={link} />
            ))}
          </div>
        </div>
      </div>
    );
  }
}

export const SidebarForUser = () => {
  const { DASHBOARD_SIDEBAR_LINKS, DASHBOARD_SIDEBAR_BOTTOM_LINKS } =
    SidebarLinksForUser();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const drawerRef = useRef(null);

  // Close drawer when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobile &&
        isDrawerOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target)
      ) {
        setDrawerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, isDrawerOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const toggleDrawer = () => {
    setDrawerOpen(!isDrawerOpen);
  };

  const toggleSubmenu = (key) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const SidebarLink = ({ link }) => {
    const { pathname } = useLocation();
    const isSubMenuOpen = openSubmenus[link.key] || false;

    const handleLinkClick = () => {
      if (isMobile) {
        setDrawerOpen(false);
      }
    };

    const renderSubMenu = () => {
      return (
        <div className='ml-8 pl-2 border-l-2 border-gray-200 dark:border-gray-700 mt-1 space-y-1'>
          {link.submenu.map((sublink) => (
            <NavLink
              key={sublink.key}
              to={`/${link.path}/${sublink.path}`}
              className={classNames(
                pathname === `/${link.path}/${sublink.path}`
                  ? activeRouteClass
                  : "",
                linkClass,
              )}
              onClick={handleLinkClick}
            >
              <span className='text-lg'>{sublink.icon}</span>
              <span className='flex-1'>{sublink.label}</span>
            </NavLink>
          ))}
        </div>
      );
    };

    return (
      <div className='mb-1'>
        {link.submenu ? (
          <div>
            <div
              id={isSubMenuOpen ? "toggle-" + link?.key : ""}
              className={classNames(
                "cursor-pointer flex items-center justify-between",
                linkClass,
                pathname.includes(`/${link.path}`) && !isSubMenuOpen
                  ? activeRouteClass
                  : "",
              )}
              onClick={() => {
                toggleSubmenu(link.key);
                if (isMobile && !isSubMenuOpen) {
                  handleLinkClick();
                }
              }}
            >
              <div className='flex items-center gap-3'>
                <span className='text-lg'>{link.icon}</span>
                <span>{link.label}</span>
              </div>
              {isSubMenuOpen ? (
                <HiOutlineChevronUp className='text-gray-500 dark:text-gray-400' />
              ) : (
                <HiOutlineChevronDown className='text-gray-500 dark:text-gray-400' />
              )}
            </div>
            {isSubMenuOpen && renderSubMenu()}
          </div>
        ) : (
          <NavLink
            to={`/${link.path}`}
            className={classNames(
              pathname === `/${link.path}` ? activeRouteClass : "",
              linkClass,
            )}
            onClick={handleLinkClick}
          >
            <span className='text-lg'>{link.icon}</span>
            <span>{link.label}</span>
          </NavLink>
        )}
      </div>
    );
  };

  if (isMobile) {
    return (
      <>
        <button
          className='text-white focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-[#170f2a] dark:border-[#170f2a] dark:hover:text-blue-700 focus:outline-none dark:focus:ring-blue-800 flex flex-col items-start'
          type='button'
          onClick={toggleDrawer}
        >
          <IoReorderThreeOutline className='text-gray-400 text-4xl self-start' />
        </button>

        <div
          ref={drawerRef}
          className={`fixed top-0 left-0 z-40 h-screen p-4 overflow-y-auto transition-transform ${
            isDrawerOpen ? "translate-x-0" : "-translate-x-full"
          } bg-white w-64 dark:bg-[#170f2a] dark:border-[#170f2a]`}
          id='drawer-disabled-backdrop'
          tabIndex='-1'
          aria-labelledby='drawer-disabled-backdrop-label'
        >
          <button
            style={{ marginLeft: "12rem" }}
            type='button'
            onClick={toggleDrawer}
            className='text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 absolute top-2.5 end-2.5 inline-flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white'
          >
            <svg
              className='w-3 h-3'
              aria-hidden='true'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 14 14'
            >
              <path
                stroke='currentColor'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6'
              />
            </svg>
            <span className='sr-only'>Close menu</span>
          </button>
          <div
            className='py-4 overflow-y-auto'
            style={{ maxHeight: 567, overflowY: "auto" }}
          >
            <div className='space-y-1'>
              {DASHBOARD_SIDEBAR_LINKS.map((link) => (
                <SidebarLink key={link.key} link={link} />
              ))}
            </div>
            <div className='mt-4 pt-4 border-t border-gray-200 dark:border-gray-800'>
              <div className='space-y-1'>
                {DASHBOARD_SIDEBAR_BOTTOM_LINKS.map((link) => (
                  <SidebarLink key={link.key} link={link} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  } else {
    return (
      <div className='bg-stone-100 w-60 p-3 flex flex-col dark:bg-[#170f2a] dark:border-[#170f2a] h-screen sticky top-0'>
        <div className='flex items-center gap-2 px-1 py-3 mb-1'>
          <img
            src='https://iconsbucket1.s3.us-east-1.amazonaws.com/logo.png'
            alt='dukanbuddy'
            className='mx-auto'
          />
        </div>
        <div
          className='flex-1 overflow-y-auto'
          style={{ maxHeight: "calc(100vh - 220px)" }}
        >
          <div className='space-y-1'>
            {DASHBOARD_SIDEBAR_LINKS.map((link) => (
              <SidebarLink key={link.key} link={link} />
            ))}
          </div>
        </div>
        <div className='mt-auto pt-4 border-t border-gray-200 dark:border-gray-800'>
          <div className='space-y-1'>
            {DASHBOARD_SIDEBAR_BOTTOM_LINKS.map((link) => (
              <SidebarLink key={link.key} link={link} />
            ))}
          </div>
        </div>
      </div>
    );
  }
};
